import { db } from '../../data/base';
import { collection, addDoc, doc, getDoc, setDoc, updateDoc, deleteDoc, arrayRemove } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { secureGetItem } from '../../data/utils';
import { mapFormToDatabase } from '../utils/catalogueModificationUtils';

// --- FIREBASE STORAGE UTILS ---
const storage = getStorage();

/**
 * Carica un file nel Firebase Storage e restituisce l'URL
 * @param {File} file - Il file da caricare
 * @param {string} filename - Il nome del file
 * @returns {Promise<string>} - L'URL del file caricato
 */
export async function uploadCatalogueAsset(file, filename) {
    const storageRef = ref(storage, `/catalogues/assets/${filename}`);
    await uploadBytes(storageRef, file);
    return getDownloadURL(storageRef);
}

/**
 * Estrae l'URL da vari tipi di oggetti immagine
 * @param {any} imageObj - L'oggetto immagine
 * @returns {string|null} - L'URL dell'immagine o null
 */
export function extractImageUrl(imageObj) {
    if (!imageObj) return null;

    // Se è una stringa, è già un URL
    if (typeof imageObj === 'string') return imageObj;

    // Se ha una proprietà url, usa quella
    if (imageObj.url) return imageObj.url;

    // Se ha una proprietà thumbUrl (come gli oggetti Ant Design Upload)
    if (imageObj.thumbUrl) return imageObj.thumbUrl;

    // Se ha una proprietà response.url (come alcuni oggetti di risposta)
    if (imageObj.response && imageObj.response.url) return imageObj.response.url;

    // Nessun URL trovato
    return null;
}

/**
 * Funzione ricorsiva per rimuovere oggetti File e convertire Date in ISO strings
 * @param {any} obj - L'oggetto da sanitizzare
 * @returns {any} - L'oggetto sanitizzato
 */
export function sanitizeForFirestore(obj) {
    if (obj === null || obj === undefined) {
        return obj;
    }

    // Gestisci Date
    if (obj instanceof Date) {
        return obj.toISOString();
    }

    // Non salvare oggetti File
    if (obj instanceof File ||
        (obj && obj.originFileObj instanceof File) ||
        (obj && obj.file instanceof File)) {
        return null;
    }

    // Gestisci array
    if (Array.isArray(obj)) {
        return obj.map(item => sanitizeForFirestore(item));
    }

    // Gestisci oggetti
    if (typeof obj === 'object') {
        const result = {};
        for (const key in obj) {
            // Salta la proprietà 'file' che potrebbe contenere oggetti File
            if (key !== 'file' && Object.prototype.hasOwnProperty.call(obj, key)) {
                const value = sanitizeForFirestore(obj[key]);
                if (value !== null && value !== undefined) {
                    result[key] = value;
                }
            }
        }
        return result;
    }

    // Valori primitivi
    return obj;
}

/**
 * Prepara gli asset (immagini) caricandoli su Firebase Storage e sostituendo i File con URL
 * @param {object} formData - I dati del form
 * @returns {Promise<object>} - I dati del form con gli asset preparati
 */
export async function prepareAssetsAndSave(formData) {
    if (!formData) return formData;

    // Creiamo una copia per non modificare l'originale
    const newFormData = { ...formData };

    // --- LOGO ---
    if (newFormData.banner?.logo) {
        newFormData.banner = { ...newFormData.banner };

        // Se è un File object diretto
        if (newFormData.banner.logo instanceof File) {
            newFormData.banner.logo = await uploadCatalogueAsset(newFormData.banner.logo, `logo_${Date.now()}_${newFormData.banner.logo.name}`);
        }
        // Se è un oggetto Ant Design Upload con originFileObj
        else if (typeof newFormData.banner.logo === 'object' && newFormData.banner.logo.originFileObj instanceof File) {
            newFormData.banner.logo = await uploadCatalogueAsset(newFormData.banner.logo.originFileObj, `logo_${Date.now()}_${newFormData.banner.logo.originFileObj.name}`);
        }
        // Se è un oggetto con URL ma non è un File
        else {
            const logoUrl = extractImageUrl(newFormData.banner.logo);
            if (logoUrl) {
                newFormData.banner.logo = logoUrl;
            }
        }
    }

    // --- BANNER IMAGE ---
    if (newFormData.banner?.bannerImage) {
        newFormData.banner = { ...newFormData.banner };

        // Se è un File object diretto
        if (newFormData.banner.bannerImage instanceof File) {
            newFormData.banner.bannerImage = await uploadCatalogueAsset(newFormData.banner.bannerImage, `banner_${Date.now()}_${newFormData.banner.bannerImage.name}`);
        }
        // Se è un oggetto Ant Design Upload con originFileObj
        else if (typeof newFormData.banner.bannerImage === 'object' && newFormData.banner.bannerImage.originFileObj instanceof File) {
            newFormData.banner.bannerImage = await uploadCatalogueAsset(newFormData.banner.bannerImage.originFileObj, `banner_${Date.now()}_${newFormData.banner.bannerImage.originFileObj.name}`);
        }
        // Se è un oggetto con URL ma non è un File
        else {
            const bannerUrl = extractImageUrl(newFormData.banner.bannerImage);
            if (bannerUrl) {
                newFormData.banner.bannerImage = bannerUrl;
            }
        }
    }

    // --- BANNER IMAGE MOBILE ---
    if (newFormData.banner?.bannerImageMobile) {
        newFormData.banner = { ...newFormData.banner };

        // Se è un File object diretto
        if (newFormData.banner.bannerImageMobile instanceof File) {
            newFormData.banner.bannerImageMobile = await uploadCatalogueAsset(newFormData.banner.bannerImageMobile, `bannerMobile_${Date.now()}_${newFormData.banner.bannerImageMobile.name}`);
        }
        // Se è un oggetto Ant Design Upload con originFileObj
        else if (typeof newFormData.banner.bannerImageMobile === 'object' && newFormData.banner.bannerImageMobile.originFileObj instanceof File) {
            newFormData.banner.bannerImageMobile = await uploadCatalogueAsset(newFormData.banner.bannerImageMobile.originFileObj, `bannerMobile_${Date.now()}_${newFormData.banner.bannerImageMobile.originFileObj.name}`);
        }
        // Se è un oggetto con URL ma non è un File
        else {
            const bannerMobileUrl = extractImageUrl(newFormData.banner.bannerImageMobile);
            if (bannerMobileUrl) {
                newFormData.banner.bannerImageMobile = bannerMobileUrl;
            }
        }
    }

    // --- FEATURED IMAGE (SEO) ---
    // Gestisci sia la struttura nidificata che quella piatta
    const hasFeaturedImageNested = newFormData.seo?.featuredImage;
    const hasFeaturedImageFlat = newFormData.featuredImage;

    // Gestisci featured image nella struttura nidificata
    if (hasFeaturedImageNested) {
        newFormData.seo = { ...newFormData.seo };

        // Se è un File object diretto
        if (newFormData.seo.featuredImage instanceof File) {
            const uploadedUrl = await uploadCatalogueAsset(newFormData.seo.featuredImage, `featured_${Date.now()}_${newFormData.seo.featuredImage.name}`);
            newFormData.seo.featuredImage = uploadedUrl;
            // Aggiorna anche il campo piatto se esiste
            newFormData.featuredImage = uploadedUrl;
        }
        // Se è un oggetto Ant Design Upload con originFileObj
        else if (typeof newFormData.seo.featuredImage === 'object' && newFormData.seo.featuredImage.originFileObj instanceof File) {
            const uploadedUrl = await uploadCatalogueAsset(newFormData.seo.featuredImage.originFileObj, `featured_${Date.now()}_${newFormData.seo.featuredImage.originFileObj.name}`);
            newFormData.seo.featuredImage = uploadedUrl;
            // Aggiorna anche il campo piatto se esiste
            newFormData.featuredImage = uploadedUrl;
        }
        // Se è un oggetto con URL ma non è un File
        else {
            const featuredImageUrl = extractImageUrl(newFormData.seo.featuredImage);
            if (featuredImageUrl) {
                newFormData.seo.featuredImage = featuredImageUrl;
                // Aggiorna anche il campo piatto se esiste
                newFormData.featuredImage = featuredImageUrl;
            }
        }
    }
    // Gestisci featured image nella struttura piatta
    else if (hasFeaturedImageFlat) {
        // Se è un File object diretto
        if (newFormData.featuredImage instanceof File) {
            const uploadedUrl = await uploadCatalogueAsset(newFormData.featuredImage, `featured_${Date.now()}_${newFormData.featuredImage.name}`);
            newFormData.featuredImage = uploadedUrl;
        }
        // Se è un oggetto Ant Design Upload con originFileObj
        else if (typeof newFormData.featuredImage === 'object' && newFormData.featuredImage.originFileObj instanceof File) {
            const uploadedUrl = await uploadCatalogueAsset(newFormData.featuredImage.originFileObj, `featured_${Date.now()}_${newFormData.featuredImage.originFileObj.name}`);
            newFormData.featuredImage = uploadedUrl;
        }
        // Se è un oggetto con URL ma non è un File
        else {
            const featuredImageUrl = extractImageUrl(newFormData.featuredImage);
            if (featuredImageUrl) {
                newFormData.featuredImage = featuredImageUrl;
            }
        }
    }

    return newFormData;
}

/**
 * Mappa i dati del form nella struttura JSON standard per Firestore
 * @param {object} formData - I dati del form
 * @returns {object} - I dati mappati nella struttura JSON standard
 */
export function mapFormDataToCatalogue(formData) {

    // Prima di tutto, usa il mapping centralizzato per convertire form -> database
    const mappedData = mapFormToDatabase(formData);

    // Creiamo un oggetto risultato con tutti i campi necessari
    const result = {
        // Campi base
        appIdIos: formData.appIdIos || null,
        appPackage: formData.appPackage || null,
        appPackageIos: formData.appPackageIos || null,
        codePath: formData.codePath || null,
        coverImage: formData.coverImage || null,
        dataConsumption: formData.dataConsumption || null,
        glassesNumber: formData.glassesNumber || null,
        hdrEnvironment: formData.hdrEnvironment || null,
        lightIntensityExponent: formData.lightIntensityExponent || null,
        list_service: formData.list_service || null,
        nameCatalog: mappedData.nameCatalog || null,
        noBackgroundLogo: formData.noBackgroundLogo || null,
        showSingleDotList: formData.showSingleDotList ?? true,
        skin: formData.skin || null,
        storageConsumption: formData.storageConsumption || null,
        storage_consumption: formData.storage_consumption || null,
        takePicTemplate: formData.takePicTemplate || null,
        webUrl: formData.webUrl || null,

        // ✅ AGGIUNGO IL CAMPO LICENSELIST PER PRESERVARLO NEI DRAFT
        licenseList: formData.licenseList || null,

        // Campo dominio web (usa il mapping centralizzato)
        webDomain: mappedData.webDomain || null,

        // --- STYLE DELLA PAGINA DEL CATALOGO ---
        catalogues_page_style: {
            // Colori personalizzati
            primaryColor: formData.customization?.primaryColor || null,
            secondaryColor: formData.customization?.secondaryColor || null,
            backgroundColor: formData.customization?.backgroundColor || '#FFFFFF',
            actionButtonColor: formData.customization?.actionButtonColor || null,
            bannerBackgroundColor: formData.customization?.bannerBackgroundColor || null,
            // Border radius per le card
            cardCornerTopLeft: formData.customization?.cardCornerTopLeft ?? 0,
            cardCornerTopRight: formData.customization?.cardCornerTopRight ?? 0,
            cardCornerBottomRight: formData.customization?.cardCornerBottomRight ?? 0,
            cardCornerBottomLeft: formData.customization?.cardCornerBottomLeft ?? 0,
            // Border radius per i pulsanti
            buttonCornerTopLeft: formData.customization?.buttonCornerTopLeft ?? 0,
            buttonCornerTopRight: formData.customization?.buttonCornerTopRight ?? 0,
            buttonCornerBottomRight: formData.customization?.buttonCornerBottomRight ?? 0,
            buttonCornerBottomLeft: formData.customization?.buttonCornerBottomLeft ?? 0,
            // Border radius per il banner
            bannerCornerTopLeft: formData.customization?.bannerCornerTopLeft ?? 0,
            bannerCornerTopRight: formData.customization?.bannerCornerTopRight ?? 0,
            bannerCornerBottomRight: formData.customization?.bannerCornerBottomRight ?? 0,
            bannerCornerBottomLeft: formData.customization?.bannerCornerBottomLeft ?? 0,
            // Logo e posizione del logo
            catalogueLogo: formData.banner?.logo || null,
            catalogueLogoPosition: formData.banner?.logoPosition || 'center',
            // ✅ USA I CAMPI CORRETTI PER LE ALTEZZE DAL MAPPING CENTRALIZZATO
            coverHeightDesktop: formData.banner?.coverHeightDesktop || 100,
            coverHeightMobile: formData.banner?.coverHeightMobile || 100,
            // Immagini del banner
            coverImg: formData.banner?.bannerImage || null,
            coverImgMobile: formData.banner?.bannerImageMobile || null,
            isLogoVisible: formData.banner?.isLogoVisible ?? true,
            isTitleVisible: formData.banner?.isTitleVisible ?? true,
            // ✅ MANTIENI ANCHE I CAMPI UI PER LA COMPATIBILITÀ
            heightOption: formData.banner?.heightOption || formData.banner?.coverHeightDesktop?.toString() || "100",
            heightMobileOption: formData.banner?.heightMobileOption || formData.banner?.coverHeightMobile?.toString() || "100",
            // ✅ RIMUOVIAMO I CAMPI LEGACY E LI IMPOSTIAMO A NULL
            coverColor: null,
            coverTemplate: null,
        },

        // Logo URL
        urlLogo: formData.banner?.logo || null,

        // --- ✅ CAMPI SEO, PERMISSIONS E BASIC USA IL MAPPING CENTRALIZZATO ---
        // I campi SEO, permissions e basic sono già correttamente mappati
        titleSeo: mappedData.titleSeo || null,
        list_keywords: mappedData.list_keywords || [],
        featuredImage: mappedData.featuredImage || null,
        metaDescription: mappedData.metaDescription || null,
        descriptionIt: mappedData.descriptionIt || null,
        descriptionEn: mappedData.descriptionEn || null,
        descriptionFr: mappedData.descriptionFr || null,
        descriptionEs: mappedData.descriptionEs || null,
        descriptionDe: mappedData.descriptionDe || null,
        isLoginRequired: mappedData.isLoginRequired ?? false,
        isIframeOnlyAccess: mappedData.isIframeOnlyAccess ?? false,
        allowedDomain: mappedData.allowedDomain || null,
        loginPassword: mappedData.loginPassword || null,
        isPublic: mappedData.isPublic ?? true,
    };

    return result;
}

/**
 * Ottiene un catalogo dal database
 * @param {string} collectionName - Il nome della collezione ('Catalogues' o 'DraftedCatalogues')
 * @param {string} catalogueId - L'ID del catalogo
 * @returns {Promise<object|null>} - I dati del catalogo o null se non esiste
 */
export async function getCatalogue(collectionName, catalogueId) {
    try {
        const catalogueRef = doc(db, collectionName, catalogueId);
        const catalogueSnap = await getDoc(catalogueRef);

        if (!catalogueSnap.exists()) {
            console.error(`Catalogue with ID ${catalogueId} not found in ${collectionName}`);
            return null;
        }

        return {
            id: catalogueId,
            ...catalogueSnap.data()
        };
    } catch (error) {
        console.error(`Error getting catalogue from ${collectionName}:`, error);
        throw error;
    }
}

/**
 * Salva un catalogo nel database
 * @param {string} collectionName - Il nome della collezione ('Catalogues' o 'DraftedCatalogues')
 * @param {object} formData - I dati del form
 * @param {object} options - Opzioni aggiuntive
 * @returns {Promise<string>} - L'ID del catalogo salvato
 */
export async function saveCatalogue(collectionName, formData, options = {}) {
    try {
        // Prepara gli asset (carica immagini, ecc.)
        const processedFormData = await prepareAssetsAndSave(formData);

        // Mappa i dati nella struttura JSON standard
        const mappedData = mapFormDataToCatalogue(processedFormData);

        // Aggiungi campi aggiuntivi dalle opzioni
        const dataToSave = {
            ...mappedData,
            ...options
        };

        // Sanitizza i dati per Firestore
        const sanitizedData = sanitizeForFirestore(dataToSave);

        // Salva il documento
        const docRef = await addDoc(collection(db, collectionName), sanitizedData);
        const catalogueId = docRef.id;

        // Se è una bozza, aggiorna la lista dei drafted catalogues nel profilo utente
        if (collectionName === 'DraftedCatalogues') {
            await updateUserDraftedCatalogues(catalogueId);
        }

        return catalogueId;
    } catch (error) {
        console.error(`Error saving catalogue to ${collectionName}:`, error);
        throw error;
    }
}

/**
 * Aggiorna un catalogo esistente
 * @param {string} collectionName - Il nome della collezione ('Catalogues' o 'DraftedCatalogues')
 * @param {string} catalogueId - L'ID del catalogo
 * @param {object} formData - I dati del form
 * @param {object} options - Opzioni aggiuntive
 * @returns {Promise<string>} - L'ID del catalogo aggiornato
 */
export async function updateCatalogue(collectionName, catalogueId, formData, options = {}) {
    try {
        // Prepara gli asset (carica immagini, ecc.)
        const processedFormData = await prepareAssetsAndSave(formData);

        // Mappa i dati nella struttura JSON standard
        const mappedData = mapFormDataToCatalogue(processedFormData);

        // Aggiungi campi aggiuntivi dalle opzioni
        const dataToUpdate = {
            ...mappedData,
            ...options
        };

        // Sanitizza i dati per Firestore
        const sanitizedData = sanitizeForFirestore(dataToUpdate);

        // Aggiorna il documento
        const catalogueRef = doc(db, collectionName, catalogueId);
        await updateDoc(catalogueRef, sanitizedData);

        return catalogueId;
    } catch (error) {
        console.error(`Error updating catalogue in ${collectionName}:`, error);
        throw error;
    }
}

/**
 * Aggiorna la lista dei drafted catalogues nel profilo utente
 * @param {string} draftId - L'ID della bozza
 * @returns {Promise<void>}
 */
export async function updateUserDraftedCatalogues(draftId) {
    try {
        let email = null;
        try {
            email = secureGetItem('email');
        } catch (e) { }

        if (email) {
            email = email.toLowerCase();
            const profileRef = doc(db, 'Profiles', email);
            const profileSnap = await getDoc(profileRef);
            let listDrafted = [];

            if (profileSnap.exists()) {
                const profileData = profileSnap.data();
                listDrafted = profileData.list_drafted_catalogues?.length
                    ? profileData.list_drafted_catalogues
                    : [];
            }

            // Aggiungi solo se non già presente
            if (!listDrafted.includes(draftId)) {
                listDrafted.push(draftId);
                await setDoc(profileRef, { list_drafted_catalogues: listDrafted }, { merge: true });
            }
        }
    } catch (err) {
        console.error('Error updating list_drafted_catalogues in profile:', err);
    }
}

/**
 * Aggiorna la lista dei cataloghi pubblicati nel profilo utente
 * @param {string} catalogueId - L'ID del catalogo pubblicato
 * @returns {Promise<void>}
 */
export async function updateUserPublishedCatalogues(catalogueId) {
    try {
        let email = null;
        try {
            email = secureGetItem('email');
        } catch (e) { }

        if (email) {
            email = email.toLowerCase();
            const profileRef = doc(db, 'Profiles', email);
            const profileSnap = await getDoc(profileRef);
            let listCatalogues = [];

            if (profileSnap.exists()) {
                const profileData = profileSnap.data();
                listCatalogues = Array.isArray(profileData.list_catalogues)
                    ? profileData.list_catalogues
                    : [];
            }

            // Aggiungi solo se non già presente
            if (!listCatalogues.includes(catalogueId)) {
                listCatalogues.push(catalogueId);
                await setDoc(profileRef, { list_catalogues: listCatalogues }, { merge: true });

                // 🔥 AGGIORNA ANCHE IL SECURE STORAGE
                await updateSecureStorageCatalogues(listCatalogues);

                // 📢 EMETTI EVENTO PER NOTIFICARE L'AGGIORNAMENTO DEI CATALOGHI
                try {
                    const catalogEvents = await import('../../utils/catalogEvents');
                    catalogEvents.default.emit(catalogEvents.CATALOG_EVENTS.CATALOGS_REFRESHED, {
                        catalogId: catalogueId,
                        action: 'published'
                    });
                } catch (eventError) {
                    console.warn('Could not emit catalog refresh event:', eventError);
                }
            }
        }
    } catch (err) {
        console.error('Error updating list_catalogues in profile:', err);
    }
}

/**
 * Aggiorna la lista dei cataloghi nel secure storage
 * @param {Array} cataloguesList - La lista aggiornata dei cataloghi
 * @returns {Promise<void>}
 */
export async function updateSecureStorageCatalogues(cataloguesList) {
    try {
        // Import dinamico per evitare problemi di import circolari
        const { secureSetItem } = await import('../../data/utils');
        secureSetItem('p_list_catalogues', cataloguesList);
    } catch (error) {
        console.error('Error updating secure storage catalogues:', error);
    }
}

/**
 * Aggiunge un catalogo alla catalogsList del ClientRef associato all'utente
 * @param {string} catalogId - ID del catalogo pubblicato
 * @returns {Promise<void>}
 */
export async function addCatalogToClientRef(catalogId) {
    try {
        // Ottieni l'email dell'utente corrente
        let email = null;
        try {
            email = secureGetItem('email');
        } catch (e) { }

        if (!email) {
            console.warn('No user email found, skipping ClientRef catalog addition');
            return;
        }

        email = email.toLowerCase();

        // Ottieni il profilo utente per trovare il clientRef
        const profileRef = doc(db, 'Profiles', email);
        const profileSnap = await getDoc(profileRef);

        if (!profileSnap.exists()) {
            console.warn('User profile not found, skipping ClientRef catalog addition');
            return;
        }

        const profileData = profileSnap.data();
        const clientRef = profileData.clientRef;

        if (!clientRef) {
            console.warn('No clientRef found in user profile, skipping ClientRef catalog addition');
            return;
        }

        // Ottieni il documento del cliente
        const clientDocRef = doc(db, 'Client', clientRef);
        const clientSnap = await getDoc(clientDocRef);

        if (!clientSnap.exists()) {
            console.warn(`Client document ${clientRef} not found, skipping catalog addition`);
            return;
        }

        const clientData = clientSnap.data();
        const currentCatalogsList = clientData.catalogsList || [];

        // Aggiungi il catalogo se non è già presente
        if (!currentCatalogsList.includes(catalogId)) {
            const updatedCatalogsList = [...currentCatalogsList, catalogId];

            // Aggiorna il documento del cliente
            await updateDoc(clientDocRef, {
                catalogsList: updatedCatalogsList
            });
        }

    } catch (error) {
        console.error('Error adding catalog to ClientRef:', error);
        // Non lanciare l'errore per non bloccare la pubblicazione se questo step fallisce
    }
}

/**
 * Deletes a drafted catalogue from the database and removes it from the user's list
 * @param {string} draftId - The ID of the draft to delete
 * @returns {Promise<boolean>} - True if deletion was successful
 */
export async function deleteDraftedCatalogue(draftId) {
    if (!draftId) {
        throw new Error("Draft ID is required to delete a draft.");
    }

    try {
        // 1. Delete the document from the DraftedCatalogues collection
        const draftRef = doc(db, 'DraftedCatalogues', draftId);
        await deleteDoc(draftRef);

        // 2. Remove the reference from the user's profile
        const email = secureGetItem('email');
        if (email) {
            const profileRef = doc(db, 'Profiles', email.toLowerCase());

            // Use arrayRemove to safely remove the draft ID from the list
            await updateDoc(profileRef, {
                list_drafted_catalogues: arrayRemove(draftId)
            });
        }

    } catch (error) {
        console.error(`Error deleting draft ${draftId}:`, error);
        throw new Error(`Failed to delete draft: ${error.message}`);
    }
}

/**
 * Updates a published catalog with changes from its draft version and cleans up the draft
 * @param {string} publishedCatalogId - The ID of the published catalog to update
 * @param {string} draftId - The ID of the draft containing the changes
 * @returns {Promise<boolean>} - True if the operation was successful
 */
export async function publishChangesFromDraft(publishedCatalogId, draftId) {
    try {
        if (!publishedCatalogId || !draftId) {
            console.error('Missing required IDs for publishing changes');
            return false;
        }

        // Get the draft data
        const draftRef = doc(db, 'DraftedCatalogues', draftId);
        const draftSnap = await getDoc(draftRef);

        if (!draftSnap.exists()) {
            return false;
        }

        // Get the published catalog data
        const catalogRef = doc(db, 'Catalogues', publishedCatalogId);
        const catalogSnap = await getDoc(catalogRef);

        if (!catalogSnap.exists()) {
            return false;
        }

        // Get the data from both documents
        const draftData = draftSnap.data();

        // Sanitize the draft data for Firestore
        const sanitizedDraftData = sanitizeForFirestore(draftData);

        // Update the published catalog with the draft data
        await updateDoc(catalogRef, sanitizedDraftData);

        // Remove the draftedCatalogRef from the published catalog
        await updateDoc(catalogRef, { draftedCatalogRef: null });

        // Delete the draft
        await deleteDraftedCatalogue(draftId);

        return true;
    } catch (error) {
        console.error('Error publishing changes from draft:', error);
        throw error;
    }
}
